import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit, Trash2, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AdminWarehouses = () => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const { toast } = useToast();

  const [warehouses, setWarehouses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    country_code: 'CO',
    city: '',
    address: '',
    postal_code: '',
    phone: '',
    email: '',
    is_active: true,
    is_default: false
  });

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API}/api/warehouses`);
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/api/countries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCountries(response.data.filter(c => c.isEnabled));
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    fetchCountries();
  }, []);

  const handleOpenDialog = (warehouse = null) => {
    if (warehouse) {
      setEditingWarehouse(warehouse);
      setFormData({
        name: warehouse.name,
        code: warehouse.code,
        country_code: warehouse.country_code,
        city: warehouse.city,
        address: warehouse.address,
        postal_code: warehouse.postal_code || '',
        phone: warehouse.phone || '',
        email: warehouse.email || '',
        is_active: warehouse.is_active,
        is_default: warehouse.is_default
      });
    } else {
      setEditingWarehouse(null);
      setFormData({
        name: '',
        code: '',
        country_code: 'CO',
        city: '',
        address: '',
        postal_code: '',
        phone: '',
        email: '',
        is_active: true,
        is_default: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      if (editingWarehouse) {
        await axios.put(`${API}/api/warehouses/${editingWarehouse.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Warehouse updated successfully' });
      } else {
        await axios.post(`${API}/api/warehouses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Warehouse created successfully' });
      }
      
      setDialogOpen(false);
      fetchWarehouses();
    } catch (error) {
      console.error('Error saving warehouse:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save warehouse',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (warehouseId) => {
    if (!confirm('Are you sure you want to delete this warehouse?')) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/api/warehouses/${warehouseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: 'Warehouse deleted successfully' });
      fetchWarehouses();
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete warehouse',
        variant: 'destructive'
      });
    }
  };

  const getCountryName = (code) => {
    const country = countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Package className="h-12 w-12 animate-spin mx-auto text-[#be1f1a]" />
          <p className="mt-4 text-gray-600">Loading warehouses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#2C2420] flex items-center gap-2">
            <Building2 className="h-8 w-8 text-[#be1f1a]" />
            Warehouses
          </h1>
          <p className="text-gray-600 mt-1">
            Manage warehouse locations and inventory allocation
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#be1f1a] hover:bg-[#a71b17]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Warehouse
        </Button>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          <strong>Multi-Warehouse Inventory:</strong> Each product can have different inventory levels per warehouse. 
          The system automatically shows products from the warehouse in the customer's country, or searches other warehouses if unavailable.
        </p>
      </div>

      {/* Warehouses Table */}
      <div className="bg-white rounded-lg shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Warehouse</TableHead>
              <TableHead>Country / City</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  No warehouses configured yet. Create your first warehouse!
                </TableCell>
              </TableRow>
            ) : (
              warehouses.map((warehouse) => (
                <TableRow key={warehouse.id}>
                  <TableCell>
                    <div className="font-medium">{warehouse.name}</div>
                    <div className="text-xs text-gray-500">{warehouse.code}</div>
                    {warehouse.is_default && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        Default
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{getFlagEmoji(warehouse.country_code)}</span>
                      <div>
                        <div>{getCountryName(warehouse.country_code)}</div>
                        <div className="text-sm text-gray-500">{warehouse.city}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{warehouse.address}</TableCell>
                  <TableCell>
                    {warehouse.phone && <div className="text-sm">{warehouse.phone}</div>}
                    {warehouse.email && <div className="text-xs text-gray-500">{warehouse.email}</div>}
                  </TableCell>
                  <TableCell>
                    {warehouse.is_active ? (
                      <span className="text-green-600 text-sm">● Active</span>
                    ) : (
                      <span className="text-gray-400 text-sm">● Inactive</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(warehouse)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(warehouse.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingWarehouse ? 'Edit Warehouse' : 'Create New Warehouse'}
            </DialogTitle>
            <DialogDescription>
              Configure warehouse details and location
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Warehouse Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Main Warehouse Colombia"
                />
              </div>
              <div>
                <Label>Warehouse Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="WH-CO-BOG-01"
                  disabled={!!editingWarehouse}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Country *</Label>
                <Select
                  value={formData.country_code}
                  onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {getFlagEmoji(country.code)} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Bogotá"
                />
              </div>
            </div>

            <div>
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Calle 100 # 20-30, Oficina 501"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="110111"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+57 300 123 4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="warehouse@store.com"
                />
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                />
                <Label>Default for this country</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
              disabled={!formData.name || !formData.code || !formData.city || !formData.address}
            >
              {editingWarehouse ? 'Update' : 'Create'} Warehouse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWarehouses;
